/* eslint-disable */
import React, { useEffect, useState } from "react";
import {
  Modal,
  Text,
  Button,
  Group,
  Select,
  Textarea,
  NumberInput,
  Stack,
  Divider,
  Badge,
} from "@mantine/core";
import { showNotification } from "@mantine/notifications";
import { useCloseSession } from "api/attendance/closeSession";

interface Session {
  sessionId: string;
  punchIn: string;
  suggestedPunchOut?: string;
  allowedHours?: number | string;
}

interface TaskOption {
  id: string;
  title: string;
}

async function fetchTaskForgeTasks(query = ""): Promise<TaskOption[]> {
  return [
    { id: "task-1", title: "Implement login" },
    { id: "task-2", title: "Fix payment bug" },
  ];
}

function isoLocalDatetime(dt: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  const year = dt.getFullYear();
  const month = pad(dt.getMonth() + 1);
  const day = pad(dt.getDate());
  const hours = pad(dt.getHours());
  const minutes = pad(dt.getMinutes());
  return `${year}-${month}-${day}T${hours}:${minutes}`;
}

function hoursBetweenISO(startISO: string, endISO: string): number {
  const start = new Date(startISO);
  const end = new Date(endISO);
  const ms = end.getTime() - start.getTime();
  if (isNaN(ms) || ms <= 0) return 0;
  return Math.round((ms / (1000 * 60 * 60)) * 100) / 100;
}

export default function UnclosedSessionModal({
  opened,
  sessions = [],
  onResolved,
}: {
  opened: boolean;
  sessions?: Session[];
  onResolved?: () => void;
}) {
  const { mutateAsync: closeSessionRequest } = useCloseSession();

  const [formState, setFormState] = useState<Record<string, any>>({});
  const [closingIds, setClosingIds] = useState<Record<string, boolean>>({});
  const [tasksOptions, setTasksOptions] = useState<
    Record<string, { value: string; label: string }[]>
  >({});
  const [loadingTasks, setLoadingTasks] = useState<Record<string, boolean>>({});

  useEffect(() => {
    const next: Record<string, any> = {};
    sessions.forEach((s: Session) => {
      const defaultPunchOut = s.suggestedPunchOut
        ? new Date(s.suggestedPunchOut)
        : new Date();
      next[s.sessionId] = {
        punchOut: isoLocalDatetime(defaultPunchOut),
        workedHours: hoursBetweenISO(s.punchIn, defaultPunchOut.toISOString()),
        allowedHours: Number(s.allowedHours ?? 8),
        overtimeTaskId: null as string | null,
        justification: "",
        requestAdjustment: false,
      };
    });
    setFormState(next);
  }, [sessions]);

  const loadTasksForSession = async (sessionId: string, q: string = "") => {
    try {
      setLoadingTasks((s) => ({ ...s, [sessionId]: true }));
      const tasks = await fetchTaskForgeTasks(q);
      const opts = tasks.map((t) => ({ value: t.id, label: t.title }));
      setTasksOptions((s) => ({ ...s, [sessionId]: opts }));
    } catch (err) {
      console.error("fetch tasks error", err);
      setTasksOptions((s) => ({ ...s, [sessionId]: [] }));
    } finally {
      setLoadingTasks((s) => ({ ...s, [sessionId]: false }));
    }
  };

  const updateField = (sessionId: string, patch: Record<string, any>) => {
    setFormState((s) => {
      const next = { ...(s[sessionId] || {}), ...patch };
      if (patch.punchOut !== undefined) {
        const session = sessions.find((x) => x.sessionId === sessionId);
        if (session) {
          next.workedHours = hoursBetweenISO(
            session.punchIn,
            new Date(patch.punchOut).toISOString()
          );
        }
      }
      return { ...s, [sessionId]: next };
    });
  };

  const validateForSession = (sessionId: string) => {
    const state = formState[sessionId];
    if (!state) return false;
    const { workedHours, allowedHours, overtimeTaskId, justification } = state;
    if (workedHours > allowedHours) {
      return !!overtimeTaskId;
    }
    if (workedHours < allowedHours) {
      return !!justification || !!state.requestAdjustment;
    }
    return true;
  };

  const buildClosePayload = (sessionId: string) => {
    const state = formState[sessionId];
    return {
      sessionId,
      punchOut: new Date(state.punchOut).toISOString(),
      // workedHours: state.workedHours,
      // allowedHours: state.allowedHours,
      // overtime:
      //   state.workedHours > state.allowedHours
      //     ? {
      //         hours:
      //           Math.round((state.workedHours - state.allowedHours) * 100) /
      //           100,
      //         taskId: state.overtimeTaskId,
      //       }
      //     : null,
      // deficit:
      //   state.workedHours < state.allowedHours
      //     ? {
      //         hours:
      //           Math.round((state.allowedHours - state.workedHours) * 100) /
      //           100,
      //         justification: state.justification,
      //         requestAdjustment: !!state.requestAdjustment,
      //       }
      //     : null,
      notes: state.justification || "Manually closed",
    };
  };

  const handleCloseSingle = async (sessionId: string) => {
    // if (!validateForSession(sessionId)) {
    //   showNotification({
    //     title: "Validation",
    //     message: "Please resolve overtime/deficit requirements before closing.",
    //     color: "yellow",
    //   });
    //   return;
    // }

    const payload = buildClosePayload(sessionId);

    try {
      setClosingIds((s) => ({ ...s, [sessionId]: true }));
      await closeSessionRequest(payload);
      showNotification({
        title: "Success",
        message: `Session ${sessionId} closed.`,
      });
      onResolved?.();
    } catch (err) {
      console.error("close session error", err);
      showNotification({
        title: "Error",
        message: `Could not close ${sessionId}.`,
      });
    } finally {
      setClosingIds((s) => {
        const next = { ...s };
        delete next[sessionId];
        return next;
      });
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={() => onResolved?.()}
      withCloseButton={true}
      closeOnClickOutside={false}
      closeOnEscape={false}
      title="Open Session(s) — Resolve required"
      centered
      overlayOpacity={0.6}
      lockScroll
    >
      <Stack spacing="sm">
        {sessions.map((s) => {
          const st = formState[s.sessionId] || {};
          const worked = (st.workedHours ?? 0) as number;
          const allowed = (st.allowedHours ??
            Number(s.allowedHours ?? 8)) as number;
          const isOvertime = worked > allowed;
          const isDeficit = worked < allowed;

          return (
            <div
              key={s.sessionId}
              style={{ padding: 12, borderRadius: 8, border: "1px solid #eee" }}
            >
              <Group position="apart">
                <Text weight={700}>Session: {s.sessionId}</Text>
                <Badge>
                  {isOvertime ? "Overtime" : isDeficit ? "Deficit" : "OK"}
                </Badge>
              </Group>

              <Text size="sm">
                Clock In: {new Date(s.punchIn).toLocaleString()}
              </Text>

              <Divider my="xs" />

              <Stack spacing={8}>
                <div>
                  <Text size="xs" weight={600}>
                    Clock Out
                  </Text>
                  <input
                    type="datetime-local"
                    value={st.punchOut || ""}
                    onChange={(e) =>
                      updateField(s.sessionId, { punchOut: e.target.value })
                    }
                    style={{
                      width: "100%",
                      padding: 8,
                      borderRadius: 6,
                      border: "1px solid #ccc",
                    }}
                  />
                </div>

                {/* <Group grow>
                  <NumberInput
                    label="Worked Hours"
                    value={worked}
                    readOnly
                    precision={2}
                  />
                  <NumberInput
                    label="Allowed Hours"
                    value={allowed}
                    onChange={(v: number | undefined) =>
                      updateField(s.sessionId, { allowedHours: v ?? 0 })
                    }
                    precision={2}
                  />
                </Group>

                {isOvertime && (
                  <div>
                    <Text size="xs" weight={600}>
                      Overtime — link to TaskForge
                    </Text>
                    <Group align="flex-end">
                      <Select
                        searchable
                        nothingFound="No tasks"
                        data={tasksOptions[s.sessionId] ?? []}
                        value={st.overtimeTaskId}
                        onFocus={() => loadTasksForSession(s.sessionId)}
                        onSearchChange={(q: string) =>
                          loadTasksForSession(s.sessionId, q)
                        }
                        onChange={(v: string | null) =>
                          updateField(s.sessionId, { overtimeTaskId: v })
                        }
                        placeholder="Select a task to link overtime"
                        style={{ flex: 1 }}
                        disabled={!!loadingTasks[s.sessionId]}
                      />
                      <Text size="xs">
                        Extra: {(worked - allowed).toFixed(2)}h
                      </Text>
                    </Group>
                  </div>
                )}

                {isDeficit && (
                  <div>
                    <Text size="xs" weight={600}>
                      Deficit — provide justification or request adjustment
                    </Text>
                    <Textarea
                      placeholder="Explain missing time or reason for early punch-out"
                      value={st.justification || ""}
                      onChange={(e) =>
                        updateField(s.sessionId, {
                          justification: e.currentTarget.value,
                        })
                      }
                      minRows={2}
                    />
                    <Group position="right" mt="xs">
                      <Button
                        variant="outline"
                        size="xs"
                        onClick={() =>
                          updateField(s.sessionId, {
                            requestAdjustment: !st.requestAdjustment,
                          })
                        }
                      >
                        {st.requestAdjustment
                          ? "Adjustment requested"
                          : "Request adjustment"}
                      </Button>
                    </Group>
                  </div>
                )} */}

                <Group position="right">
                  <Button
                    onClick={() => handleCloseSingle(s.sessionId)}
                    loading={!!closingIds[s.sessionId]}
                    // disabled={!validateForSession(s.sessionId)}
                  >
                    Close Session
                  </Button>
                </Group>
              </Stack>
            </div>
          );
        })}
      </Stack>
    </Modal>
  );
}
